import { formatDate, fromEasternWallDate, shouldHideSiteNameOnDispatchUi, toEasternDateKey, toEasternWallDate } from '../../util';
import { MutableRefObject, useEffect } from 'react';
import { BryntumScheduler } from '@bryntum/scheduler-react';
import { endOfDay, format, startOfDay } from 'date-fns';

// The dispatch board's schedulers hold Eastern "wall" Dates (device-local fields read as the Eastern clock), so the
// timeline is laid out in Eastern time on any device. Convert API instants on the way in and back on the way out;
// everything read from a scheduler record or the board's day (startDate, endDate, schedule.startDate) is a wall Date.

/** API dispatches -> scheduler events, with start/end as Eastern wall Dates. */
export const toSchedulerEvents = (events: any[]) =>
    events.map((event) => ({
        ...event,
        startDate: event.startDate ? toEasternWallDate(event.startDate) : event.startDate,
        endDate: event.endDate ? toEasternWallDate(event.endDate) : event.endDate,
    }));

/** A scheduler wall Date -> the real instant to send to the API. */
export const fromSchedulerDate = (date?: Date | string | null) => (date ? fromEasternWallDate(new Date(date)) : date);

/**
 * Dates to save for a scheduler event: its wall start/end kept within the board's day (wallStart/wallEnd, for the
 * record) and the matching real instants for the API (startDate/endDate).
 */
export const getDispatchSaveDates = (start: Date, end: Date, boardDay: Date) => {
    const dayStart = startOfDay(boardDay);
    const dayEnd = endOfDay(boardDay);
    const wallStart = start < dayStart ? dayStart : start;
    const wallEnd = end > dayEnd ? dayEnd : end;
    return {
        wallStart,
        wallEnd,
        startDate: fromSchedulerDate(wallStart) as Date,
        endDate: fromSchedulerDate(wallEnd) as Date,
    };
};

/** Formats a scheduler wall Date (its device-local fields already read as Eastern). */
export const formatSchedulerTime = (date?: Date | string | null, formatString: string = 'h:mm a') => (date ? format(new Date(date), formatString) : '');

/** Whether a wall Date's day (the board's day, or a scheduler date) is today or later in Eastern. */
export const isNotOutOfDate = (date: Date) => {
    return format(date, 'yyyy-MM-dd') >= toEasternDateKey(new Date());
};

export const isInThePast = (date: Date) => {
    return !isNotOutOfDate(date);
};

/** After yard/onsite (or fallback) is merged onto the schedule day: if that time is already past, use current time (minute precision). Wall Dates. */
export function clampDispatchStartToNotBeforeNow(candidate: Date): Date {
    const now = toEasternWallDate(new Date());
    const candidateDateTime = new Date(candidate);

    now.setSeconds(0, 0);
    candidateDateTime.setSeconds(0, 0);

    return candidateDateTime < now ? new Date(now) : candidateDateTime;
}

/** Start (wall Date) for a line item dropped on the board: its onsite, else yard, else current Eastern time, on the board's day, never before now. */
export function getDroppedDispatchStart(boardDay: Date, onsiteTime?: Date | string | null, yardTime?: Date | string | null): Date {
    const source = onsiteTime ? toEasternWallDate(onsiteTime) : yardTime ? toEasternWallDate(yardTime) : toEasternWallDate(new Date());
    const start = new Date(boardDay.getFullYear(), boardDay.getMonth(), boardDay.getDate(), source.getHours(), source.getMinutes(), 0, 0);
    return clampDispatchStartToNotBeforeNow(start);
}

const EASTERN_NOW_ID = 'easternCurrentTime';

/** Draws the "now" line at the current Eastern time (as a wall Date), replacing Bryntum's device-clock line. */
export function useEasternCurrentTimeLine(scheduler?: { project?: any; features?: any } | null) {
    useEffect(() => {
        const timeRangeStore = scheduler?.project?.timeRangeStore;
        if (!timeRangeStore) {
            return;
        }

        // showCurrentTimeLine is part of the scheduler's saved state, so a stored "true" would bring the device-clock line back
        if (scheduler?.features?.timeRanges) {
            scheduler.features.timeRanges.showCurrentTimeLine = false;
        }

        const update = () => {
            const now = toEasternWallDate(new Date());
            const data = { startDate: now, endDate: now, name: format(now, 'HH:mm'), cls: 'b-sch-current-time' };
            const existing = timeRangeStore.getById(EASTERN_NOW_ID);
            if (existing) {
                existing.set(data);
            } else {
                timeRangeStore.add({ id: EASTERN_NOW_ID, ...data });
            }
        };

        update();
        const intervalId = setInterval(update, 30 * 1000);
        return () => {
            clearInterval(intervalId);
            const existing = timeRangeStore.getById(EASTERN_NOW_ID);
            if (existing) {
                timeRangeStore.remove(existing);
            }
        };
    }, [scheduler]);
}

export const getToolTipHeader = (_version: string, data: any, eventRecord?: any) => {
    if (data) {
        const name = data?.name;
        const dateRange = `${formatSchedulerTime(eventRecord?.startDate)} - ${formatSchedulerTime(eventRecord?.endDate)}`;
        const equipmentLabel = data?.equipment ? `(${data?.equipment?.equipmentType?.type ?? ''} #${data?.equipment?.number ?? ''})` : '';
        const line1 = `${name} ${equipmentLabel}&nbsp;&nbsp;&nbsp;&nbsp;${dateRange}`;
        const line2 = `${data?.description ?? ''} (LI #${data?.orderLineItemNumber}, Order #${data?.orderNumber}, Job #${data?.jobNumber ?? '???'})`;
        return `${line1}<br/>${line2}`;
    }
    return '';
};

export const getHtml = (date: Date, data: any, schedulerRef: MutableRefObject<BryntumScheduler>, eventRecord?: any) => {
    let toolTip = `<div class="html-container">
                        <div class="flex-box">
                            ${getToolTipData(date, data, eventRecord)}
                        </div>
                        <div class="divider-vertical"></div>
                        <div class="flex-box">
                            ${getMemos(data, schedulerRef)}
                        </div>
                    </div>`;
    return toolTip;
};

const getMemos = (data: any, schedulerRef: MutableRefObject<BryntumScheduler>) => {
    const memoHtml = `<div class="memo-container">
                        <div class="memo-body">
                            <div class="memo-row">
                                <span class="memo-label">Dispatch/Billing Memo:</span>
                                <span class="memo-value">${data.dispatchBillingMemo ?? ''}</span>
                            </div>
                            <div class="divider"></div>
                            <div class="memo-row">
                                <span class="memo-label">Order Memo:</span>
                                <span class="memo-value">${data.driverMemo ?? ''}</span>
                            </div>
                            <div class="divider"></div>
                            <div class="memo-row">
                                <span class="memo-label">Driver Comment:</span>
                                <span class="memo-value">${data.driverComment ?? ''}</span>
                            </div>
                        </div>
                    </div>`;
    return memoHtml;
};

const getContactInfo = (data: any) => {
    let contactInfo = ``;
    const contactFields: string[] = [];
    
    if (data?.contactName) {
        contactFields.push(`<span class="tooltip-label">Contact:</span><span class="tooltip-value">${data?.contactName ?? ''}</span>`);
    }
    if (data?.contactEmail) {
        contactFields.push(`<span class="tooltip-label">Email:</span><span class="tooltip-value"><a href="mailto:${data?.contactEmail ?? ''}">${data?.contactEmail ?? ''}</a></span>`);
    }
    if (data?.contactCellPhone) {
        contactFields.push(`<span class="tooltip-label">Cell:</span><span class="tooltip-value"><a href="tel:${data?.contactCellPhone ?? ''}">${data?.contactCellPhone ?? ''}</a></span>`);
    }
    if (data?.contactLandlinePhone) {
        contactFields.push(`<span class="tooltip-label">Landline:</span><span class="tooltip-value"><a href="tel:${data?.contactLandlinePhone ?? ''}">${data?.contactLandlinePhone ?? ''}</a></span>`);
    }
    if (data?.contactFax) {
        contactFields.push(`<span class="tooltip-label">Fax:</span><span class="tooltip-value"><a href="tel:${data?.contactFax ?? ''}">${data?.contactFax ?? ''}</a></span>`);
    }
    if (contactFields.length > 0) {
        contactInfo += `<div class="divider"></div>`;
    }
    // Group fields into rows of two columns
    for (let i = 0; i < contactFields.length; i += 2) {
        const leftField = contactFields[i];
        const rightField = contactFields[i + 1] || '';
        contactInfo += `<div class="tooltip-row">
                            <div class="tooltip-column">${leftField}</div>
                            <div class="tooltip-column">${rightField}</div>
                        </div>`;
    }
    
    if (data?.contactMemo) {
        contactInfo += `<div class="memo-row" style="max-height: 75px; overflow-y: auto;">
                            <span class="memo-label">Memo:</span>
                            <span class="memo-value">${data?.contactMemo ?? ''}</span>
                        </div>`;
    }
    return contactInfo;
};

const getOrderedByInfo = (data: any) => { 
    let orderedByInfo = ``;
    const orderedByFields: string[] = [];
    
    if (data?.orderedByContactName) {
        orderedByFields.push(`<span class="tooltip-label">Ordered By:</span><span class="tooltip-value">${data?.orderedByContactName ?? ''}</span>`);
    }
    if (data?.orderedByContactEmail) {
        orderedByFields.push(`<span class="tooltip-label">Email:</span><span class="tooltip-value"><a href="mailto:${data?.orderedByContactEmail ?? ''}">${data?.orderedByContactEmail ?? ''}</a></span>`);
    }
    if (data?.orderedByContactCellPhone) {
        orderedByFields.push(`<span class="tooltip-label">Cell:</span><span class="tooltip-value"><a href="tel:${data?.orderedByContactCellPhone ?? ''}">${data?.orderedByContactCellPhone ?? ''}</a></span>`);
    }
    if (data?.orderedByContactLandlinePhone) {
        orderedByFields.push(`<span class="tooltip-label">Landline:</span><span class="tooltip-value"><a href="tel:${data?.orderedByContactLandlinePhone ?? ''}">${data?.orderedByContactLandlinePhone ?? ''}</a></span>`);
    }
    if (data?.orderedByContactFax) {
        orderedByFields.push(`<span class="tooltip-label">Fax:</span><span class="tooltip-value"><a href="tel:${data?.orderedByContactFax ?? ''}">${data?.orderedByContactFax ?? ''}</a></span>`);
    }
    if (orderedByFields.length > 0) {
        orderedByInfo += `<div class="divider"></div>`;
    }
    
    // Group fields into rows of two columns
    for (let i = 0; i < orderedByFields.length; i += 2) {
        const leftField = orderedByFields[i];
        const rightField = orderedByFields[i + 1] || '';
        orderedByInfo += `<div class="tooltip-row">
                            <div class="tooltip-column">${leftField}</div>
                            <div class="tooltip-column">${rightField}</div>
                        </div>`;
    }
    
    if (data?.orderedByContactMemo) {
        orderedByInfo += `<div class="memo-row" style="max-height: 75px; overflow-y: auto;">
                            <span class="memo-label">Memo:</span>
                            <span class="memo-value">${data?.orderedByContactMemo ?? ''}</span>
                        </div>`;
    }
    return orderedByInfo;
};

const getToolTipData = (date: Date, data: any, eventRecord?: any) => {
    const lineItemType = data?.lineItemType ?? data?.orderLineItem?.lineItemType ?? data?.orderLineItem?.lineItem?.type;
    const hideSiteName = shouldHideSiteNameOnDispatchUi(lineItemType);

    let dispatchingIntervalCount = data?.dispatchingIntervalCount ?? 0;
    let equipmentType = data?.equipment?.equipmentType?.type ?? '';
    let dispatchingIntervalTime = `${dispatchingIntervalCount} ${equipmentType} every ${data?.dispatchingIntervalTime} min.`;

    let status = '';
    if (eventRecord) {
        if (eventRecord?.isOrderInReview) {
            status = 'In Review';
        } else if (eventRecord?.isCompleted) {
            status = 'Complete';
        } else if (eventRecord?.canceledOn) {
            status = 'Canceled';
        } else {
            status = eventRecord?.released ? 'Released' : 'Unreleased';
        }
    }

    const toolTipHtml = `<div class="tooltip-container">
                            <div class="tooltip-body">
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Status:</span>
                                    <span class="tooltip-value">${status}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Customer:</span>
                                    <span class="tooltip-value">${data?.customerName ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Customer Number:</span>
                                    <span class="tooltip-value">${data?.customerNumber ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Orderer:</span>
                                    <span class="tooltip-value">${data?.orderer ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Project Name:</span>
                                    <span class="tooltip-value">${data?.projectName ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Job Name:</span>
                                    <span class="tooltip-value">${data?.jobName ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">PO Number:</span>
                                    <span class="tooltip-value">${data?.poNumber ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Job #:</span>
                                    <span class="tooltip-value">${data?.jobNumber ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <div class="tooltip-row">
                                        <span class="tooltip-label">Order #:</span>
                                        <span class="tooltip-value">${data?.orderNumber ?? ''}</span>
                                    </div>
                                    <div class="tooltip-row">
                                        <span class="tooltip-label">Line Item #:</span>
                                        <span class="tooltip-value">${data?.orderLineItemNumber}</span>
                                    </div>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Order Created:</span>
                                    <span class="tooltip-value">${formatDate(data?.orderCreatedOn, 'Pp')}</span>
                                </div>
                                ${getContactInfo(data)}
                                ${getOrderedByInfo(data)}
                                <div class="divider"></div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Description:</span>
                                    <span class="tooltip-value">${data?.description ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Requirements:</span>
                                    <span class="tooltip-value">${data?.requirements ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Haul Quantity:</span>
                                    <span class="tooltip-value">${data?.haulQuantityLabel ?? ''}</span> 
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Equipment Type:</span>
                                    <span class="tooltip-value">${data?.equipment?.equipmentType?.type ?? ''}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Truck #:</span>
                                    <span class="tooltip-value">${data?.equipment?.number ?? ''}</span>
                                </div>
                                <div class="divider"></div>
                                <div class="tooltip-row">
                                    <div class="tooltip-row">
                                        <span class="tooltip-label">Yard Time:</span>
                                        <span class="tooltip-value">${data?.yardTime ? formatDate(data?.yardTime, 'h:mm:ss aa') : 'ASAP'}</span>
                                    </div>
                                    <div class="tooltip-row">
                                        <span class="tooltip-label">Onsite Time:</span>
                                        <span class="tooltip-value">${data?.startDate ? formatSchedulerTime(data?.startDate, 'h:mm:ss aa') : 'ASAP'}</span>
                                    </div>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Interval Time:</span>
                                    <span class="tooltip-value">${dispatchingIntervalTime}</span>
                                </div>
                                <div class="divider"></div>
                                ${hideSiteName ? '' : `<div class="tooltip-row">
                                    <span class="tooltip-label">Site:</span>
                                    <span class="tooltip-value">${data?.siteName ?? ''}</span>
                                </div>`}
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Haul To:</span>
                                    <span class="tooltip-value">${data?.haulToAddressDispatchCard}</span>
                                </div>
                                <div class="tooltip-row">
                                    <span class="tooltip-label">Haul From:</span>
                                    <span class="tooltip-value">${data?.haulFromAddressDispatchCard}</span>
                                </div>
                            </div>
                        </div>`;

    return toolTipHtml;
};
